import { useState, useRef } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

interface Citation {
  page_number: number
  bounding_boxes_inches: [number, number, number, number][]
  text?: string
}

const API_BASE = 'http://localhost:8000'

export function PdfTester() {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [localPdfUrl, setLocalPdfUrl] = useState<string | null>(null)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [citation, setCitation] = useState<Citation | null>(null)
  const [scale, setScale] = useState(1.5)

  const [uploading, setUploading] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [extractError, setExtractError] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number>(0)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0]
    if (!selected) return
    setFile(selected)
    setLocalPdfUrl(null)
    setDocumentId(null)
    setCitation(null)
    setUploadError(null)
    setExtractError(null)
  }

  async function handleUpload() {
    if (!file) return
    setUploading(true)
    setUploadError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API_BASE}/api/sources/upload`, {
        method: 'POST',
        body: formData,
      })
      if (!res.ok) throw new Error(`Upload failed: ${res.status} ${res.statusText}`)
      const data = await res.json()
      setDocumentId(data.source_id)
      const objUrl = URL.createObjectURL(file)
      setLocalPdfUrl(objUrl)
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err))
    } finally {
      setUploading(false)
    }
  }

  async function handleExtract() {
    if (!documentId || !query.trim()) return
    setExtracting(true)
    setExtractError(null)
    setCitation(null)
    try {
      const res = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: documentId, query }),
      })
      if (!res.ok) throw new Error(`Extract failed: ${res.status} ${res.statusText}`)
      const data = await res.json()
      setCitation(data.citation)
    } catch (err) {
      setExtractError(err instanceof Error ? err.message : String(err))
    } finally {
      setExtracting(false)
    }
  }

  const currentPage = citation?.page_number ?? 1

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'monospace', fontSize: 13 }}>
      {/* ── Left Panel ── */}
      <div
        style={{
          width: 280,
          minWidth: 280,
          padding: 16,
          borderRight: '1px solid #ccc',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          background: '#f7f7f7',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>PDF Coordinate Tester</h2>
        <small style={{ color: '#666' }}>Dev sandbox — not for production</small>

        <hr style={{ margin: '4px 0' }} />

        {/* File input */}
        <label style={{ fontWeight: 600 }}>1. Select PDF</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={handleFileChange}
          style={{ fontSize: 12 }}
        />
        {file && (
          <small style={{ color: '#444', wordBreak: 'break-all' }}>
            {file.name} ({(file.size / 1024).toFixed(1)} KB)
          </small>
        )}

        {/* Upload */}
        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          style={btnStyle(!file || uploading)}
        >
          {uploading ? 'Uploading…' : 'Upload to API'}
        </button>
        {uploadError && <small style={{ color: 'red' }}>{uploadError}</small>}
        {documentId && (
          <small style={{ color: 'green', wordBreak: 'break-all' }}>
            ✓ doc_id: {documentId}
          </small>
        )}

        <hr style={{ margin: '4px 0' }} />

        {/* Query */}
        <label style={{ fontWeight: 600 }}>2. Query</label>
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. What is the role of acrylate copolymer?"
          rows={4}
          style={{
            resize: 'vertical',
            fontFamily: 'monospace',
            fontSize: 12,
            padding: 6,
            border: '1px solid #ccc',
            borderRadius: 4,
          }}
        />

        {/* Extract */}
        <button
          onClick={handleExtract}
          disabled={!documentId || !query.trim() || extracting}
          style={btnStyle(!documentId || !query.trim() || extracting)}
        >
          {extracting ? 'Extracting…' : 'Test Extract'}
        </button>
        {extractError && <small style={{ color: 'red' }}>{extractError}</small>}

        {/* Citation result */}
        {citation && (
          <div
            style={{
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: 4,
              padding: 8,
              fontSize: 11,
            }}
          >
            <strong>Citation result:</strong>
            <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(citation, null, 2)}
            </pre>
          </div>
        )}

        <hr style={{ margin: '4px 0' }} />

        {/* Zoom */}
        <label style={{ fontWeight: 600 }}>Zoom</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)))} style={smallBtnStyle}>
            −
          </button>
          <span style={{ minWidth: 36, textAlign: 'center' }}>{scale.toFixed(2)}×</span>
          <button onClick={() => setScale((s) => Math.min(4, +(s + 0.25).toFixed(2)))} style={smallBtnStyle}>
            +
          </button>
        </div>

        {numPages > 0 && (
          <small style={{ color: '#666' }}>
            Showing page {currentPage} / {numPages}
          </small>
        )}
      </div>

      {/* ── Right Panel (PDF Viewer) ── */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          background: '#888',
          display: 'flex',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        {!localPdfUrl ? (
          <div
            style={{
              color: '#ddd',
              alignSelf: 'center',
              textAlign: 'center',
              fontSize: 14,
            }}
          >
            Upload a PDF to preview it here.
          </div>
        ) : (
          <Document
            file={localPdfUrl}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            onLoadError={(err) => setUploadError(`PDF load error: ${err.message}`)}
          >
            {/* Wrapper keeps overlays positioned relative to the rendered page */}
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer
                renderAnnotationLayer
              />

              {/* Coordinate overlays */}
              {citation?.bounding_boxes_inches?.map((box, i) => {
                const [minX, minY, maxX, maxY] = box
                return (
                  <div
                    key={i}
                    style={{
                      position: 'absolute',
                      left: minX * 72 * scale,
                      top: minY * 72 * scale,
                      width: (maxX - minX) * 72 * scale,
                      height: (maxY - minY) * 72 * scale,
                      backgroundColor: 'rgba(255, 255, 0, 0.4)',
                      mixBlendMode: 'multiply',
                      pointerEvents: 'none',
                      border: '1px solid rgba(200, 160, 0, 0.6)',
                    }}
                  />
                )
              })}
            </div>
          </Document>
        )}
      </div>
    </div>
  )
}

// ── tiny style helpers ──────────────────────────────────────────────────────

function btnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '7px 12px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? '#ccc' : '#1a1a1a',
    color: disabled ? '#888' : '#fff',
    border: 'none',
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: 12,
    fontWeight: 600,
  }
}

const smallBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  cursor: 'pointer',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 16,
  lineHeight: 1,
}
